"use strict";

import { setStoredProjectFile } from "../utils/storage";
import {
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Tabs,
  Tab,
  Container,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import classes from "../Assets/Styles/Global.module.css";
import Loader from "../Components/Loader.js";
import {
  GET_USERS,
  UPDATE_PROJECT,
  FACE_DETECTOR,
  UPDATE_DIALOGUE_USED_MODEL,
  CREATE_ALL_VOCALS_AUTOMATICALLY,
  TRANSLATE,
} from "../ApiEndpoints.js";
import { getProjectById } from "../features/projects/api.js";
import { languages } from "../Data/languages.js";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getLanguageName,
  getRoleOptionLabel,
  getTaskName,
} from "../utils/generalFunctions.js";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import { ConfirmToast } from "react-confirm-toast";
import { toast } from "react-toastify";
import UserSelectAutocomplete from "../Components/UserSelectAutoComplete/index.js";
import { Store } from "../Store";
import {
  DEFAULT_VOICE_ENGINE,
  VOICE_ENGINE_OPTIONS,
  getSupportedVoiceEngine,
  isSupportedVoiceEngine,
} from "../Data/voiceEngines.js";

const ProjectEditScreen = () => {
  const params = useParams();
  const { id: projectId } = params;
  const navigate = useNavigate();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;
  const [videoFile, setVideoFile] = useState("");
  const [dialogueAudioFile, setDialogueAudioFile] = useState("");
  // const [dialogueAudioURL, setDialogueAudioURL] = useState();
  const [fxFile, setFxFile] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectOriginalLang, setProjectOriginalLang] =
    useState("Original Language");
  const [projectFinalLang, setProjectFinalLang] = useState("Final Languages");
  const [projectFinalLangs, setProjectFinalLangs] = useState([]);
  const [uploadedAudioTypes, setUploadedAudioTypes] = useState([]);
  const [projectCreationDate, setProjectCreationDate] = useState("");
  const [defaultVoiceModel, setDefaultVoiceModel] = useState("");
  const [defaultVoiceLanguage, setDefaultVoiceLanguage] = useState("");
  const [isSettingDefaultVoiceModel, setIsSettingDefaultVoiceModel] =
    useState(false);
  const [createTakesMode, setCreateTakesMode] = useState("soft");
  const [createTakesLanguage, setCreateTakesLanguage] = useState("");
  const [translateMode, setTranslateMode] = useState("soft");
  const [translateLanguage, setTranslateLanguage] = useState("");

  // Removed unused state such as videoURL, employees and selectEmployee after
  // adopting the UserSelectAutocomplete component, which manages its own data.

  const [selectRole, setSelectRole] = useState("Select role");
  const [displaySelection, setDisplaySelection] = useState([]);
  const [upLoader, setUpLoader] = useState(false);
  const [facesFileExists, setFacesFileExists] = useState(false);
  const [isFaceDetecting, setIsFaceDetecting] = useState(false);
  const [selectedUserObj, setSelectedUserObj] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("configuration");
  const initialSnapshot = React.useRef("");
  const savingRef = React.useRef(false);
  const availableDefaultLanguages = React.useMemo(() => {
    // Ensure unique language codes for the default selector.
    return Array.from(new Set(projectFinalLangs || []));
  }, [projectFinalLangs]);
  /**
   * Serializes a snapshot of project data for change detection.
   * @param {object} snapshot - Current project state to serialize.
   * @returns {string} JSON string representation of the snapshot.
   */
  const serializeSnapshot = React.useCallback((snapshot) => {
    // Convert the snapshot object into a string for comparisons.
    return JSON.stringify(snapshot);
  }, []);

  const ALL_ROLES = [
    "transcriber",
    "translator",
    "performancedirection",
    "lipqualityassurance",
    "speakerfaceidentification",
    "qualityassurance",
  ];

  /**
   * Ensures role input is treated as an array to simplify downstream handling.
   * @param {string|string[]} role - Role code(s) to normalize.
   * @returns {string[]} Normalized list of role codes.
   */
  const normalizeRoles = (role) => {
    // Normalize a single role into a list for consistent processing.
    return Array.isArray(role) ? role : [role];
  };

  /**
   * Formats role codes into a human-readable string for table display.
   * @param {string|string[]} role - Role code(s) to format.
   * @returns {string} Comma-separated list of role labels.
   */
  const formatRoleLabel = (role) => {
    // De-duplicate roles so we do not repeat labels in the UI.
    const uniqueRoles = Array.from(new Set(normalizeRoles(role)));
    // Convert role codes to display names for table presentation.
    return uniqueRoles.map((roleCode) => getTaskName(roleCode)).join(", ");
  };

  /**
   * Submits the default voice model selection for a project language.
   */
  const handleSetDefaultVoiceModel = async () => {
    // Confirm that a model is selected before submitting.
    if (!defaultVoiceModel) {
      toast.error("Please select a default voice model.");
      return;
    }

    // Confirm that a language is selected before submitting.
    if (!defaultVoiceLanguage) {
      toast.error("Please select a language.");
      return;
    }

    try {
      // Lock the UI while the request is in flight.
      setIsSettingDefaultVoiceModel(true);
      // Fall back from legacy hydrated values before submitting.
      const supportedDefaultVoiceModel =
        getSupportedVoiceEngine(defaultVoiceModel);
      // Submit the project-level default selection to the API.
      await axios.post(UPDATE_DIALOGUE_USED_MODEL, {
        projectId,
        languageCode: defaultVoiceLanguage,
        usedModel: supportedDefaultVoiceModel,
      });
      // Keep the selector aligned if a legacy value was remapped.
      setDefaultVoiceModel(supportedDefaultVoiceModel);
      // Confirm success to the user.
      toast.success("Default voice model updated.");
    } catch (error) {
      // Log and surface any request failures.
      console.error("Failed to set default voice model:", error);
      toast.error("Failed to set default voice model.");
    } finally {
      // Re-enable the UI after the request completes.
      setIsSettingDefaultVoiceModel(false);
    }
  };

  /**
   * Requests vocal take generation for a selected language and mode.
   */
  const handleCreateTakes = async () => {
    // Ensure a language has been selected before making a request.
    if (!createTakesLanguage) {
      toast.error("Please select a language.");
      return;
    }

    // Based on the Create/Regenerate takes flow from the project admin actions page.
    // We are adopting the "takes" naming convention for this workflow going forward.
    const runGenerate = async () => {
      try {
        // TODO: Confirm the target language already has translations for every dialogue line.
        // Submit the generation request to the backend.
        await axios.post(CREATE_ALL_VOCALS_AUTOMATICALLY, {
          projectId,
          language: createTakesLanguage,
          projectName,
          // TODO: Remove dependency on creationDate for takes requests.
          creationDate: projectCreationDate,
          soft: createTakesMode === "soft",
        });
        // Confirm the request has been sent.
        toast.success(
          `Takes have been requested for ${getLanguageName(
            createTakesLanguage
          )}`
        );
      } catch (error) {
        // Surface errors to the user when generation fails.
        toast.error("An error has occurred while creating translated takes");
      }
    };

    // Require confirmation for hard regenerations.
    if (createTakesMode === "hard") {
      const toastId = toast.info(
        ({ closeToast }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span>
              Hard re-populate will overwrite existing audio. Continue?
            </span>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  toast.dismiss(toastId);
                  await runGenerate();
                }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toast.dismiss(toastId)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ),
        { autoClose: false, closeOnClick: false }
      );
    } else {
      // Soft mode can run immediately without confirmation.
      await runGenerate();
    }
  };

  /**
   * Requests translation creation or regeneration for the selected language.
   */
  const handleCreateTranslation = async () => {
    // Ensure a language has been selected before making a request.
    if (!translateLanguage) {
      toast.error("Please select a language.");
      return;
    }

    // Based on the Create/Regenerate translation flow from the project admin actions page.
    const runTranslate = async () => {
      try {
        // TODO: Confirm transcriptions are ready for all imported audio before translation.
        await axios.post(TRANSLATE, {
          projectId,
          newLanguage: translateLanguage,
          mode: translateMode,
        });
        toast.success(
          `Translations have been requested for ${getLanguageName(
            translateLanguage
          )}`
        );
      } catch (error) {
        console.error("Failed to request translations:", error);
        toast.error("An error occurred while creating translations");
      }
    };

    // Require confirmation for hard regenerations.
    if (translateMode === "hard") {
      const toastId = toast.info(
        ({ closeToast }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span>
              Hard re-translate will overwrite existing translations. Continue?
            </span>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  toast.dismiss(toastId);
                  await runTranslate();
                }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toast.dismiss(toastId)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ),
        { autoClose: false, closeOnClick: false }
      );
    } else {
      // Soft mode can run immediately without confirmation.
      await runTranslate();
    }
  };

  useEffect(() => {
    // Redirect non-admins away from the manage screen.
    if (!userInfo || !userInfo.isAdmin) {
      navigate("/");
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    // Normalize legacy hydrated voice model values before submit actions.
    if (
      defaultVoiceModel &&
      !isSupportedVoiceEngine(defaultVoiceModel)
    ) {
      setDefaultVoiceModel(DEFAULT_VOICE_ENGINE);
    }
  }, [defaultVoiceModel]);

  useEffect(() => {
    // Clear the selected language if it was removed from the project.
    if (
      defaultVoiceLanguage &&
      !availableDefaultLanguages.includes(defaultVoiceLanguage)
    ) {
      setDefaultVoiceLanguage("");
    }
  }, [availableDefaultLanguages, defaultVoiceLanguage]);

  const currentSnapshot = React.useMemo(() => {
    // Collect the state needed to detect unsaved changes.
    return {
      projectName,
      projectOriginalLang,
      projectFinalLangs,
      displaySelection,
      videoFile,
      dialogueAudioFile,
      fxFile,
      uploadedAudioTypes,
      facesFileExists,
    };
  }, [
    projectName,
    projectOriginalLang,
    projectFinalLangs,
    displaySelection,
    videoFile,
    dialogueAudioFile,
    fxFile,
    uploadedAudioTypes,
    facesFileExists,
  ]);

  const isDirty = React.useMemo(() => {
    // Skip dirty checks until the initial snapshot is set.
    if (!initialSnapshot.current) return false;
    // Compare the current state to the stored snapshot.
    return serializeSnapshot(currentSnapshot) !== initialSnapshot.current;
  }, [currentSnapshot, serializeSnapshot]);

  /**
   * Prompts the user when attempting to leave with unsaved changes and
   * keeps the active tab on the configuration panel if they cancel.
   * @returns {boolean} Whether navigation away from the page is allowed.
   */
  const promptUnsavedChanges = React.useCallback(() => {
    // Ask the user to confirm leaving the page.
    const confirmed = window.confirm(
      "You have unsaved changes. Are you sure you want to leave this page?"
    );

    // Keep the user on the configuration tab if they cancel.
    if (!confirmed) {
      setActiveTab("configuration");
    }

    // Return the user's decision for navigation handlers.
    return confirmed;
  }, [setActiveTab]);

  useEffect(() => {
    // Capture the initial snapshot once data has loaded.
    if (!dataLoaded || initialSnapshot.current) return;
    initialSnapshot.current = serializeSnapshot(currentSnapshot);
  }, [dataLoaded, currentSnapshot, serializeSnapshot]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Request the project data from the API.
        // const response = await axios.get(
        //   `http://localhost:4001/api/projects/${projectId}`
        // );

        const response = await getProjectById(projectId);

        const projectData = {
          ...response.data,
          id: response.data.id || projectId,
        };
        // Populate state from the response payload.
        setProjectName(projectData.name || "");
        setProjectOriginalLang(projectData.orginalLang || "Original Language");
        setProjectFinalLangs(projectData.finalLang || []);
        setDisplaySelection(projectData.employes || []);
        setVideoFile(projectData.videoUrl || "");
        setDialogueAudioFile(projectData.dialogueAudioUrl || "");
        setFxFile(projectData.fxUrl || "");
        setUploadedAudioTypes(projectData.uploadedAudioTypes || []);
        setProjectCreationDate(projectData.creationDate || "");
        setFacesFileExists(projectData.facesFileExists || false);
        ctxDispatch({ type: "PROJECT_FILE", payload: projectData });
        setStoredProjectFile(projectData);
        setDataLoaded(true);
      } catch (err) {
        // Log request failures for debugging.
        console.log(err);
      }
    };
    // Trigger the project data load on mount and when the ID changes.
    fetchData();
  }, [projectId, ctxDispatch]);

  // Previously this component fetched the employee list on mount, but
  // UserSelectAutocomplete now retrieves its own data so that effect was removed.

  /**
   * Persists project updates and resets the dirty state after a successful save.
   * @param {React.FormEvent} e - Submit event from the configuration form.
   */
  const handleUpdateProject = async (e) => {
    // Prevent native form submission and show the loader.
    setUpLoader(true);
    e.preventDefault();

    try {
      // Send the updated project payload to the API.
      await axios.post(UPDATE_PROJECT, {
        name: projectName,
        employes: displaySelection,
        orginalLang: projectOriginalLang,
        finalLang: projectFinalLangs,
        videoUrl: videoFile,
        dialogueAudioUrl: dialogueAudioFile,
        uploadedAudioTypes: uploadedAudioTypes,
        fxUrl: fxFile,
        id: projectId,
      });
      // Reset the dirty tracking snapshot on success.
      initialSnapshot.current = serializeSnapshot(currentSnapshot);
      savingRef.current = false;
      // Notify the user that the save succeeded.
      toast.success("Saved");
    } catch (error) {
      // Log and surface save failures.
      console.log(error);
      toast.error("Failed to save");
    } finally {
      // Hide the loader regardless of request outcome.
      setUpLoader(false);
    }
  };

  /**
   * Removes all entries for a given employee name from the selection list.
   * @param {string} name - Employee name to remove.
   */
  const handleRemoveEmployees = (name) => {
    // Filter out the employee matching the provided name.
    const newItems = displaySelection.filter((item) => item.name !== name);
    // Persist the filtered list to state.
    setDisplaySelection(newItems);
  };

  /**
   * Removes a specific language code from the list of final languages.
   * @param {string} code - Language code to remove.
   */
  const handleRemoveLanguage = (code) => {
    // Remove the specified language from the selection.
    const newItems = projectFinalLangs.filter(
      (languageCode) => languageCode !== code
    );
    // Persist the updated list to state.
    setProjectFinalLangs(newItems);
  };

  /**
   * Confirms lip sync and face detection initiation to prevent accidental runs.
   */
  const handleFacesFileAction = () => {
    // Use a stable toast ID so we do not stack multiple confirmations.
    const TOAST_ID = "faces-file-confirmation";

    // Abort if the confirmation toast is already visible.
    if (toast.isActive(TOAST_ID)) return;

    // Show a confirmation prompt before starting detection.
    toast.info(
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          width: "100%",
        }}
      >
        <p style={{ width: "100%", margin: "0 0 8px 0" }}>
          You are starting the lip sync and face detection process. Are you sure
          you want to proceed?
        </p>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            handleFaceDetectionStart();
            // Close the confirmation toast after the action is confirmed.
            toast.dismiss(TOAST_ID);
          }}
        >
          Confirm
        </Button>
      </div>,
      {
        toastId: TOAST_ID,
        autoClose: false,
        closeOnClick: false,
        position: "top-center",
      }
    );
  };

  /**
   * Triggers the face detection process and updates UI state based on the result.
   */
  const handleFaceDetectionStart = async () => {
    try {
      // Mark the action as in progress to disable the checkbox.
      setIsFaceDetecting(true);

      // Kick off the face detection job on the backend.
      await axios.post(FACE_DETECTOR, {
        videoUrl: videoFile, // using videoFile instead of videoURL since that's what's in the response
        projectName: projectName,
      });

      // Mark the results as available after a successful request.
      setFacesFileExists(true);

      // Notify the user that the job succeeded.
      toast.success("Face detection completed successfully");
    } catch (error) {
      // Report failures and reset the UI state.
      console.error("Face detection error:", error);
      toast.error("Failed to process face detection");
      // Reset checkbox in case of error
      setFacesFileExists(false);
    } finally {
      // Clear the in-progress flag when the request finishes.
      setIsFaceDetecting(false);
    }
  };

  useEffect(() => {
    // Skip interception when there are no unsaved changes.
    if (!isDirty) return;

    const confirmLeave = () => {
      // Only confirm if a save isn't already in progress.
      if (!savingRef.current && isDirty) {
        return promptUnsavedChanges();
      }

      // Allow navigation by default.
      return true;
    };

    // Intercept programmatic navigations (push/replace)
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;

    const wrap = (fn) =>
      function wrapped(state, title, url) {
        // Block navigation if the user declines the confirmation prompt.
        if (!confirmLeave()) return;
        // Continue with navigation when confirmed.
        return fn.apply(window.history, arguments);
      };

    window.history.pushState = wrap(originalPush);
    window.history.replaceState = wrap(originalReplace);

    // Intercept back/forward buttons
    const onPopState = () => {
      // Prevent back/forward navigation when the user cancels the prompt.
      if (!confirmLeave()) {
        // undo the back/forward
        window.history.go(1);
      }
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      // Restore the original history methods and listeners.
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
      window.removeEventListener("popstate", onPopState);
    };
  }, [isDirty, promptUnsavedChanges]);

  useEffect(() => {
    // Only register the unload handler when there are unsaved changes.
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      // Trigger the browser confirmation prompt.
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    };
    // Register the unload listener.
    window.addEventListener("beforeunload", handleBeforeUnload);
    // Clean up the listener on unmount or when changes are saved.
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const actionButtonSx = {
    fontWeight: 600,
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.7)",
      backgroundColor: "rgba(31, 55, 105, 0.5)",
    },
  };
  const actionSectionSx = {
    border: "1px solid #e4e7ef",
    borderRadius: "var(--radius-md)",
    p: 2,
    backgroundColor: "#fafbfc",
  };

  return (
    <Container
      maxWidth="xl"
      style={{ fontFamily: "var(--font-body)" }}
      sx={{ py: 4 }}
    >
      <Loader Load={upLoader} />
      <ToastContainer position="top-center" />

      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Grid item className={classes["header"]}>
          Manage
        </Grid>

        <Box>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            sx={{
              mb: "-1px",
              minHeight: 48,
              px: { xs: 0, sm: 1 },
              "& .MuiTabs-flexContainer": { columnGap: 2 },
            }}
          >
            <Tab label="Configuration" value="configuration" />
            <Tab label="Assets" value="assets" />
            <Tab label="Actions" value="actions" />
          </Tabs>

          <Box
            sx={{
              border: "1px solid #dfe3eb",
              borderRadius: "var(--radius-md)",
              backgroundColor: "white",
              p: 3,
              mt: 0,
            }}
          >
            {activeTab === "configuration" && (
              <form onSubmit={handleUpdateProject}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Project Title"
                      type="text"
                      value={projectName}
                      // onChange={(e) => setProjectName(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Select
                        value={projectOriginalLang}
                        onChange={(e) => {
                          // Persist the selected original language.
                          setProjectOriginalLang(e.target.value);
                        }}
                      >
                        <MenuItem value="Original Language" disabled={true}>
                          Original Language
                        </MenuItem>
                        {languages.map((language) => (
                          <MenuItem key={language.code} value={language.code}>
                            {language.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <p>* Experimental Transcription Support</p>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <FormControl fullWidth>
                          <Select
                            value={projectFinalLang}
                            onChange={(e) => {
                              // Persist the selected final language candidate.
                              setProjectFinalLang(e.target.value);
                            }}
                          >
                            <MenuItem value="Final Languages" disabled={true}>
                              Final Languages
                            </MenuItem>
                            {languages.map((language) => (
                              <MenuItem
                                key={language.code}
                                value={language.code}
                              >
                                {language.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() => {
                            // Skip if no language is selected.
                            if (projectFinalLang === "") return;
                            // Add the selected language to the list.
                            setProjectFinalLangs([
                              ...projectFinalLangs,
                              projectFinalLang,
                            ]);
                            // Reset the dropdown after adding.
                            setProjectFinalLang("");
                          }}
                          size="small"
                        >
                          Add
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container gap={1}>
                          {projectFinalLangs.map((language, index) => (
                            <Grid
                              item
                              key={index}
                              sx={{
                                bgcolor: "lightblue",
                                minWidth: "70px",
                                borderRadius: "var(--radius-md)",
                              }}
                            >
                              <Grid item marginLeft={1}>
                                {getLanguageName(language)}
                              </Grid>
                              <Grid item marginLeft={2}>
                                <IconButton
                                  onClick={() => handleRemoveLanguage(language)}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={facesFileExists}
                          disabled={facesFileExists || isFaceDetecting}
                          onChange={
                            !facesFileExists ? handleFacesFileAction : undefined
                          }
                        />
                      }
                      label="Lip Sync"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} sm={6} md={5}>
                        <UserSelectAutocomplete
                          onUserSelect={(user) => {
                            // Store the complete user object when selected.
                            setSelectedUserObj(user);
                          }}
                          existingUsers={displaySelection}
                          fetchUsersUrl={GET_USERS}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <Select
                            value={selectRole}
                            onChange={(event) => {
                              // Persist the selected role for assignment.
                              setSelectRole(event.target.value);
                            }}
                          >
                            <MenuItem value="Select role" disabled>
                              Select role
                            </MenuItem>
                            <MenuItem value="allRoles">All Roles</MenuItem>
                            <MenuItem value="transcriber">
                              {getRoleOptionLabel("transcriber")}
                            </MenuItem>
                            <MenuItem value="translator">
                              {getRoleOptionLabel("translator")}
                            </MenuItem>
                            <MenuItem value="performancedirection">
                              {getRoleOptionLabel("performancedirection")}
                            </MenuItem>
                            <MenuItem value="lipqualityassurance">
                              {getRoleOptionLabel("lipqualityassurance")}
                            </MenuItem>
                            <MenuItem value="speakerfaceidentification">
                              {getRoleOptionLabel("speakerfaceidentification")}
                            </MenuItem>
                            <MenuItem value="qualityassurance">
                              {getRoleOptionLabel("qualityassurance")}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() => {
                            // Ensure a user is selected before assignment.
                            if (!selectedUserObj) {
                              toast.error("Please select an employee.");
                              return;
                            }

                            // Ensure a role is selected before assignment.
                            if (selectRole === "Select role") {
                              toast.error("Please select a role for the user.");
                              return;
                            }

                            // Expand roles when the "all roles" option is chosen.
                            const requestedRoles =
                              selectRole === "allRoles"
                                ? ALL_ROLES
                                : [selectRole];

                            // Check whether the requested role(s) already exist.
                            const alreadySelected = displaySelection.some(
                              (item) =>
                                item.name === selectedUserObj.name &&
                                requestedRoles.every((role) =>
                                  normalizeRoles(item.role).includes(role)
                                )
                            );

                            // Prevent duplicate role assignments.
                            if (alreadySelected) {
                              toast.error(
                                "This employee already has the selected role."
                              );
                              return;
                            }

                            // Build the new employee assignment record.
                            const userItem = {
                              id: selectedUserObj.id,
                              name: selectedUserObj.name,
                              role:
                                selectRole === "allRoles"
                                  ? ALL_ROLES
                                  : selectRole,
                              selectedUserObj,
                            };

                            // Persist the new assignment and reset role selection.
                            setDisplaySelection([
                              ...displaySelection,
                              userItem,
                            ]);
                            setSelectRole("Select role");
                            // setSelectedUserObj(null);
                          }}
                        >
                          Add Employee
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Table
                      size="small"
                      sx={{
                        minWidth: 650,
                        bgcolor: "white",
                        "& .MuiTableCell-root": { py: 0.5, px: 1 },
                      }}
                    >
                      <TableHead bgcolor="#5570dc">
                        <TableRow>
                          <TableCell>NAME</TableCell>
                          <TableCell>ROLE</TableCell>
                          <TableCell>ACTIONS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displaySelection.map((user, index) => (
                          <TableRow key={index}>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{formatRoleLabel(user.role)}</TableCell>
                            <TableCell>
                              <Button
                                color="error"
                                onClick={() => handleRemoveEmployees(user.name)}
                              >
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>

                  <Grid item xs={12}>
                    <Button variant="contained" type="submit">
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}

            {activeTab === "assets" && (
              <Table
                size="small"
                sx={{
                  minWidth: 650,
                  bgcolor: "white",
                  "& .MuiTableCell-root": { py: 0.5, px: 1 },
                }}
              >
                <TableHead bgcolor="#5570dc">
                  <TableRow>
                    <TableCell>FILE TYPE</TableCell>
                    <TableCell>URL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Original Video</TableCell>
                    <TableCell>
                      {videoFile ? (
                        <a
                          href={videoFile}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2" }}
                        >
                          {videoFile}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Dialogue Audio</TableCell>
                    <TableCell>
                      {dialogueAudioFile ? (
                        <a
                          href={dialogueAudioFile}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2" }}
                        >
                          {dialogueAudioFile}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>FX Audio</TableCell>
                    <TableCell>
                      {fxFile ? (
                        <a
                          href={fxFile}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2" }}
                        >
                          {fxFile}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                  {uploadedAudioTypes.map((audio, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {audio.audioType.charAt(0).toUpperCase() +
                          audio.audioType.slice(1)}
                        {audio.channelType && ` (${audio.channelType} channel)`}
                        {audio.uploadType && ` - ${audio.uploadType}`}
                      </TableCell>
                      <TableCell>
                        {audio.url ? (
                          <a
                            href={audio.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2" }}
                          >
                            {audio.url}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {activeTab === "actions" && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {/* Based on the Create/Regenerate translation controls from the project admin actions page. */}
                    <Box sx={actionSectionSx}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Create/Regenerate Translation
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel
                              id="create-translation-language-label"
                              shrink
                            >
                              Language
                            </InputLabel>
                            <Select
                              labelId="create-translation-language-label"
                              label="Language"
                              value={translateLanguage}
                              onChange={(e) =>
                                setTranslateLanguage(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a language</em>
                              </MenuItem>
                              {availableDefaultLanguages.length === 0 && (
                                <MenuItem value="" disabled>
                                  No languages added
                                </MenuItem>
                              )}
                              {availableDefaultLanguages.map((languageCode) => (
                                <MenuItem
                                  key={languageCode}
                                  value={languageCode}
                                >
                                  {getLanguageName(languageCode)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <RadioGroup
                            row
                            value={translateMode}
                            onChange={(e) => setTranslateMode(e.target.value)}
                          >
                            <FormControlLabel
                              value="soft"
                              control={<Radio size="small" />}
                              label="Soft"
                            />
                            <FormControlLabel
                              value="hard"
                              control={<Radio size="small" />}
                              label="Hard"
                            />
                            <Tooltip
                              title="Soft preserves existing work, only generating missing data. Hard generates entirely new data and may replace existing work."
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                aria-label="Translation mode help"
                              >
                                <Typography component="span" variant="body2">
                                  ?
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={!translateLanguage}
                            onClick={handleCreateTranslation}
                            sx={actionButtonSx}
                          >
                            Create Translation
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={actionSectionSx}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Set Default Voice Model
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <FormControl fullWidth>
                            <InputLabel id="default-voice-model-label" shrink>
                              Default Voice Model
                            </InputLabel>
                            <Select
                              labelId="default-voice-model-label"
                              label="Default Voice Model"
                              value={defaultVoiceModel}
                              onChange={(e) =>
                                setDefaultVoiceModel(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a model</em>
                              </MenuItem>
                              {VOICE_ENGINE_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel
                              id="default-voice-language-label"
                              shrink
                            >
                              Language
                            </InputLabel>
                            <Select
                              labelId="default-voice-language-label"
                              label="Language"
                              value={defaultVoiceLanguage}
                              onChange={(e) =>
                                setDefaultVoiceLanguage(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a language</em>
                              </MenuItem>
                              {availableDefaultLanguages.length === 0 && (
                                <MenuItem value="" disabled>
                                  No languages added
                                </MenuItem>
                              )}
                              {availableDefaultLanguages.map((languageCode) => (
                                <MenuItem
                                  key={languageCode}
                                  value={languageCode}
                                >
                                  {getLanguageName(languageCode)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={isSettingDefaultVoiceModel}
                            onClick={handleSetDefaultVoiceModel}
                            sx={actionButtonSx}
                          >
                            Set Default
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    {/* Based on the Create/Regenerate takes controls from the project admin actions page. */}
                    <Box sx={actionSectionSx}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Create/Regenerate Takes
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel id="create-takes-language-label" shrink>
                              Language
                            </InputLabel>
                            <Select
                              labelId="create-takes-language-label"
                              label="Language"
                              value={createTakesLanguage}
                              onChange={(e) =>
                                setCreateTakesLanguage(e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Select a language</em>
                              </MenuItem>
                              {availableDefaultLanguages.length === 0 && (
                                <MenuItem value="" disabled>
                                  No languages added
                                </MenuItem>
                              )}
                              {availableDefaultLanguages.map((languageCode) => (
                                <MenuItem
                                  key={languageCode}
                                  value={languageCode}
                                >
                                  {getLanguageName(languageCode)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <RadioGroup
                            row
                            value={createTakesMode}
                            onChange={(e) => setCreateTakesMode(e.target.value)}
                          >
                            <FormControlLabel
                              value="soft"
                              control={<Radio size="small" />}
                              label="Soft"
                            />
                            <FormControlLabel
                              value="hard"
                              control={<Radio size="small" />}
                              label="Hard"
                            />
                            <Tooltip
                              title="Soft preserves existing work, only generating missing data. Hard generates entirely new data and may replace existing work."
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                aria-label="Takes mode help"
                              >
                                <Typography component="span" variant="body2">
                                  ?
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={!createTakesLanguage}
                            onClick={handleCreateTakes}
                            sx={actionButtonSx}
                          >
                            Create Takes
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ProjectEditScreen;
